import type { RouteLocationNormalized } from 'vue-router';

export const APP_NAME = 'MX Tool';

const SHOP_DETAIL_PATH = /^\/shops\/[^/]+/;

export interface ShopTitleContext {
  name?: string | null;
  id?: string | number | null;
}

export function isShopDetailRoute (route: RouteLocationNormalized): boolean {
  return typeof route.path === 'string' && SHOP_DETAIL_PATH.test(route.path);
}

export function resolveShopTitleContext (
  route: RouteLocationNormalized,
  shopData?: { name?: string; id?: number } | null,
): ShopTitleContext | null {
  if (!isShopDetailRoute(route)) {
    return null;
  }

  return {
    name: shopData?.name ?? null,
    id: shopData?.id ?? route.params.id ?? null,
  };
}

function hasShopTitleContext (shop?: ShopTitleContext | null): shop is ShopTitleContext {
  return Boolean(shop && (shop.name || (shop.id != null && shop.id !== '')));
}

function formatShopLabel (shop: ShopTitleContext): string | null {
  const id = shop.id != null && shop.id !== '' ? String(shop.id) : null;

  if (shop.name && id) {
    return `${shop.name} (${id})`;
  }

  if (shop.name) {
    return shop.name;
  }

  if (id) {
    return `(${id})`;
  }

  return null;
}

export function formatPageTitle (pageTitle: string, shop?: ShopTitleContext | null): string {
  if (hasShopTitleContext(shop)) {
    const shopLabel = formatShopLabel(shop);

    if (shopLabel) {
      return `${pageTitle} | ${shopLabel} | ${APP_NAME}`;
    }
  }

  return `${pageTitle} | ${APP_NAME}`;
}

export function resolveDocumentTitle (
  route: RouteLocationNormalized,
  shop?: ShopTitleContext | null,
): string {
  const pageTitle = typeof route.meta?.title === 'string' ? route.meta.title : APP_NAME;
  const effectiveShop = isShopDetailRoute(route) ? shop : null;
  return formatPageTitle(pageTitle, effectiveShop);
}

export function applyDocumentTitle (
  route: RouteLocationNormalized,
  shop?: ShopTitleContext | null,
): void {
  document.title = resolveDocumentTitle(route, shop);
}

export function setPageTitle (
  pageTitle: string,
  route: RouteLocationNormalized,
  shop?: ShopTitleContext | null,
): void {
  document.title = resolveDocumentTitle(
    { ...route, meta: { ...route.meta, title: pageTitle } },
    shop,
  );
}
